import React from 'react';
import { Stack, Text, Card, Group, Badge, Table, rem, Skeleton } from '@mantine/core';
import type { DataQuery, SeriesPoint } from '../../types';
import type { PivotedMeasurementRecord } from '../../types/pivoted';

type Props = {
  t: (k: string, opts?: unknown) => string;
  seriesItems: SeriesPoint[];
  data: PivotedMeasurementRecord[];
  query: DataQuery;
  baseMetrics: string[];
  weatherMetrics?: string[];
  dateOnlyTicks: boolean;
  isMobile: boolean;
  translateSeriesLabel: (label: string) => string;
  unitForMetricId: (metricId: string) => string;
  sensorValueTypeById: Map<string, string>;
  loading?: boolean;
};

const DataTableSection: React.FC<Props> = ({
  t,
  seriesItems,
  data,
  query,
  baseMetrics,
  weatherMetrics = [],
  dateOnlyTicks,
  isMobile,
  translateSeriesLabel,
  unitForMetricId,
  sensorValueTypeById,
  loading = false,
}) => {
  // Show skeleton loader when loading
  if (loading) {
    return (
      <Stack gap="md">
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Text fw={500} mb="md">{t('data.table.title') || 'Daten'}</Text>
          <Skeleton height={40} mb="sm" />
          {[...Array(5)].map((_, i) => (
            <Group key={i} mb="sm">
              <Skeleton height={30} width={120} mr="md" />
              {[...Array(3)].map((_, j) => (
                <Skeleton key={j} height={30} width={80} mr="sm" />
              ))}
            </Group>
          ))}
        </Card>
      </Stack>
    );
  }

  const sensorIds: string[] = Array.isArray(query.sensorId)
    ? (query.sensorId as string[])
    : (query.sensorId ? [query.sensorId as string] : []);
  const gatewayIds: string[] = Array.isArray(query.gatewayId)
    ? (query.gatewayId as string[])
    : (query.gatewayId ? [query.gatewayId as string] : []);

  const derivedSensors: string[] = Array.from(new Set((data || []).map((r) => r?.sensor?.id).filter(Boolean) as string[])).slice(0, 50);
  const effectiveSensorIds: string[] = sensorIds.length > 0 ? sensorIds : derivedSensors;
  const derivedGateways: string[] = Array.from(new Set((data || []).map((r) => r?.gateway?.id).filter(Boolean) as string[])).slice(0, 50);
  const effectiveGatewayIds: string[] = gatewayIds.length > 0 ? gatewayIds : derivedGateways;

  const sensorsByGatewayRender = new Map<string, Set<string>>();
  const sensorsNoGw: string[] = [];
  (data || []).forEach((r) => {
    const sid = r?.sensor?.id as string | undefined;
    if (!sid) return;
    const gid = (r?.gateway?.id as string | undefined) ?? undefined;
    if (gid) {
      if (!sensorsByGatewayRender.has(gid)) sensorsByGatewayRender.set(gid, new Set<string>());
      sensorsByGatewayRender.get(gid)!.add(sid);
    } else if (!sensorsNoGw.includes(sid)) {
      sensorsNoGw.push(sid);
    }
  });

  // Extract actual metric keys from seriesItems (same logic as chart)
  const actualMetricKeys = new Set<string>();
  seriesItems.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (key !== 'timestamp' && typeof item[key] === 'object' && item[key] !== null) {
        actualMetricKeys.add(key);
      }
    });
  });

  // Use actual metrics from data, excluding weather metrics
  const dataMetrics = Array.from(actualMetricKeys).filter(k => !(weatherMetrics as string[]).includes(k));

  type ColGroup = { key: string; title: string; cols: string[] };
  let groups: ColGroup[] = [];

  // Use actual data metrics instead of baseMetrics
  groups = [{ key: 'default', title: '', cols: dataMetrics }];

  const allColumns = [...groups.flatMap((g) => g.cols), ...weatherMetrics];

  const fmtTs = (iso: string) => {
    try {
      const ms = Date.parse(iso);
      if (!Number.isFinite(ms)) return iso;
      const d = new Date(ms);
      return dateOnlyTicks ? d.toLocaleDateString() : d.toLocaleString();
    } catch {
      return iso;
    }
  };

  type Boxplot = { low: number; q1: number; median: number; q3: number; high: number };
  const hasMedian = (val: unknown): val is Boxplot => {
    return typeof val === 'object' && val !== null && 'median' in (val as Record<string, unknown>) && typeof (val as Record<string, unknown>).median === 'number';
  };

  const unitForMetric = (label: string): string => {
    // Extract sensor ID from label (format: "metricName (sensorId)")
    const match = label.match(/\(([^)]+)\)$/);
    if (match) {
      const sensorId = match[1];
      const valueTypeValue = sensorValueTypeById.get(sensorId);
      if (valueTypeValue) return valueTypeValue;
    }
    
    // Fallback to unitForMetricId
    const base = label.includes(' (') ? label.substring(0, label.indexOf(' (')) : label;
    const u = unitForMetricId(base);
    if (u) return u;
    
    return '';
  };

  return (
    isMobile ? (
      <Stack p="sm" gap="sm">
        {seriesItems.length === 0 ? (
          <Text c="dimmed">{t('data.table.noData') || 'Keine Daten gefunden'}</Text>
        ) : (
          seriesItems.map((row) => {
            const entries = allColumns.map((label) => {
              const v = (row as Record<string, SeriesPoint[string]>)[label];
              const median = hasMedian(v) ? v.median : undefined;
              const unit = unitForMetric(label);
              const txt = (typeof median === 'number' && Number.isFinite(median)) ? `${median.toFixed(2)}${unit ? ` ${unit}` : ''}` : '';
              return { label, value: txt };
            }).filter((e) => e.value !== '');
            return (
              <Card key={row.timestamp} withBorder p="sm">
                <Stack gap={6}>
                  <Group justify="space-between">
                    <Text fw={600}>{fmtTs(row.timestamp)}</Text>
                    <Badge variant="light" size="sm">{t('data.chart.tableTitle') || 'Chart-Daten'}</Badge>
                  </Group>
                  {entries.length === 0 ? (
                    <Text c="dimmed" size="sm">{t('data.chart.noData') || 'Keine Daten zum Anzeigen'}</Text>
                  ) : (
                    <Stack gap={4}>
                      {entries.map((e) => (
                        <Group key={`${row.timestamp}-${e.label}`} justify="space-between">
                          <Text size="sm" c="dimmed" style={{ maxWidth: '60%' }}>{translateSeriesLabel(e.label)}</Text>
                          <Text size="sm" fw={600} style={{ textAlign: 'right' }}>{e.value}</Text>
                        </Group>
                      ))}
                    </Stack>
                  )}
                </Stack>
              </Card>
            );
          })
        )}
      </Stack>
    ) : (
      <div style={{ overflowX: 'auto' }}>
        <Table striped highlightOnHover withRowBorders={false} stickyHeader stickyHeaderOffset={0} style={{ fontSize: isMobile ? rem(12) : undefined }}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th rowSpan={2} style={{ position: 'sticky', left: 0, background: 'var(--mantine-color-body)' }}>{t('data.table.time') || 'Zeit'}</Table.Th>
              {groups.map((g) => (
                <Table.Th key={`gh-${g.key}`} colSpan={g.cols.length} style={{ textAlign: 'center' }}>
                  {g.title}
                </Table.Th>
              ))}
            </Table.Tr>
            <Table.Tr>
              {allColumns.map((label) => (
                <Table.Th key={`h-${label}`} style={{ whiteSpace: 'nowrap' }}>{translateSeriesLabel(label)}</Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {seriesItems.map((row) => (
              <Table.Tr key={row.timestamp}>
                <Table.Td style={{ whiteSpace: 'nowrap', position: 'sticky', left: 0, background: 'var(--mantine-color-body)' }}>{t('data.table.time') || 'Zeit'}: {fmtTs(row.timestamp)}</Table.Td>
                {allColumns.map((label) => {
                  const v = (row as Record<string, SeriesPoint[string]>)[label];
                  const median = hasMedian(v) ? v.median : undefined;
                  const unit = unitForMetric(label);
                  const txt = (typeof median === 'number' && Number.isFinite(median)) ? `${median.toFixed(2)}${unit ? ` ${unit}` : ''}` : '';
                  return <Table.Td key={`c-${row.timestamp}-${label}`} style={{ whiteSpace: 'nowrap' }}>{txt}</Table.Td>;
                })}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </div>
    )
  );
};

export default DataTableSection;
